import { ALL_PAID_BREAK_BEHAVIORS } from '@careerstart/wae-common/schema/models/position-template/enums/paidBreakBehaviors';
import { isValidWorkersCompCode } from '@careerstart/wae-common/schema/models/position-template/subSchemas/workersCompCodeSchema';

import { SECTION_IDS, SECTIONS } from '../constants';

import { getPunchDistanceError } from './additionalInfoValidation';
import { isShiftValid } from './shiftValidation';

const hasItems = (list) => Array.isArray(list) && list.length > 0;
const hasCorporation = (values) => !!values.corporation;
const hasName = (values) => !!values.name?.trim();
const hasIndustry = (values) => !!values.industry;
const hasDescription = (values) => !!values.description?.trim();
const isValidRate = (rate) => Number.parseFloat(rate) > 0;
const hasAllRates = (rates) =>
  isValidRate(rates?.reg) && isValidRate(rates?.ot) && isValidRate(rates?.dt);
const hasOnlyValidLocations = (values) =>
  hasItems(values.locations) &&
  values.locations.every((location) => isValidWorkersCompCode(location?.compCode));
const hasOnlyValidShifts = (values) =>
  hasItems(values.shifts) && values.shifts.every((shift) => isShiftValid(shift, values.shifts));

export const CREATE_REQUIREMENTS = [
  {
    id: 'corporation',
    labelKey: 'positionTemplate.create.beforeYouSave.corporation',
    isMet: hasCorporation,
  },
  {
    id: 'positionName',
    labelKey: 'positionTemplate.create.beforeYouSave.positionName',
    isMet: hasName,
  },
  {
    id: 'industry',
    labelKey: 'positionTemplate.create.beforeYouSave.industry',
    isMet: hasIndustry,
  },
  {
    id: 'description',
    labelKey: 'positionTemplate.create.beforeYouSave.description',
    isMet: hasDescription,
  },
  {
    id: 'locationsWithCompCodes',
    labelKey: 'positionTemplate.create.beforeYouSave.locationsWithCompCodes',
    isMet: hasOnlyValidLocations,
  },
  {
    id: 'shifts',
    labelKey: 'positionTemplate.create.beforeYouSave.shifts',
    isMet: hasOnlyValidShifts,
  },
];

export const APPROVE_REQUIREMENTS = [
  {
    id: 'rates',
    labelKey: 'positionTemplate.create.beforeYouSave.rates',
    isMet: (values) => hasAllRates(values.payRates) && hasAllRates(values.billRates),
  },
];

const evaluate = (requirements, values) =>
  requirements.map(({ id, labelKey, isMet }) => ({ id, labelKey, isMet: isMet(values) }));

export const getCreateReadiness = (values) => {
  const createChecks = evaluate(CREATE_REQUIREMENTS, values);
  const approveChecks = evaluate(APPROVE_REQUIREMENTS, values);

  return {
    createChecks,
    approveChecks,
    canCreate: createChecks.every((check) => check.isMet),
    canApprove: approveChecks.every((check) => check.isMet),
  };
};

export const SECTION_COMPLETION_RULES = {
  [SECTION_IDS.POSITION_DETAILS]: (values) =>
    hasCorporation(values) && hasName(values) && hasIndustry(values) && hasDescription(values),
  [SECTION_IDS.LOCATIONS]: hasOnlyValidLocations,
  [SECTION_IDS.PAY_RATES]: (values) =>
    hasAllRates(values.payRates) && hasAllRates(values.billRates),
  [SECTION_IDS.SHIFTS]: hasOnlyValidShifts,
  [SECTION_IDS.REQUIREMENTS]: ({ requirements }) =>
    hasItems(requirements?.certifications) || hasItems(requirements?.skills),
  [SECTION_IDS.ADDITIONAL_INFO]: (values) => !getPunchDistanceError(values.additionalInfo),
  [SECTION_IDS.LEGAL_INFO]: (values) =>
    ALL_PAID_BREAK_BEHAVIORS.includes(values.legalInfo?.paidBreakBehavior),
};

export const getSectionStatuses = (values) =>
  SECTIONS.map((section) => ({
    ...section,
    isComplete: !!SECTION_COMPLETION_RULES[section.id]?.(values),
  }));
